import { selectRanges } from 'api/System/selectors'
import type { Range } from 'api/System/types'
import { Ranges } from 'api/System/types'
import { selectPermission } from 'api/User/selectors'
import { PERMISSIONS } from 'api/User/types'
import classNames from 'classnames'
import { CommonButton } from 'CommonButton'
import { CloseButton } from 'components'
import type { Moment } from 'moment'
import type { ChangeEvent, MouseEvent, SyntheticEvent } from 'react'
import { useEffect } from 'react'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from 'store/types'
import { momentValidDateFormat } from 'utils'
import Transition from 'utils/SlideTransition'

import DatePicker from '@mui/lab/DatePicker'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'

import { selectDateRange, toggleDatePickerDialog } from './actions'
import { TypeAnalysis } from './constants'
import { selectClosedPeriod, selectDateCurrent, selectIsOpen, selectTypeAnalysis } from './selectors'
import type { ClosedPeriods } from './types'
import useSelectedRangeText from './useSelectedRangeText'
import useStyles from './useStyles'

type LocaleLang = 'namePL' | 'nameEN'

const FROM_RANGES = [Ranges.WEEK, Ranges.MONTH, Ranges.QUARTER, Ranges.HALFYEAR, Ranges.YEAR]
const CLOSED_PERIODS: ClosedPeriods[] = [Ranges.WEEK, Ranges.WEEK2, Ranges.MONTH, Ranges.QUARTER, Ranges.HALFYEAR]

function DatePickerDialog() {
  const showRanges = useSelector(selectPermission(PERMISSIONS.RANGES))
  const lang = useSelector((state: State) => state.i18nState.lang)
  const ranges = useSelector(selectRanges)
  const initialTypeAnalysis = useSelector(selectTypeAnalysis)
  const initialClosedPeriod = useSelector(selectClosedPeriod)
  const isOpen = useSelector(selectIsOpen)
  const dateCurrent = useSelector(selectDateCurrent)
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()
  const toggleModal = useCallback(() => dispatch(toggleDatePickerDialog()), [dispatch])
  const [date, setDate] = useState<Moment | null>(moment(dateCurrent))
  const maxDate = moment(new Date())
  const typeAnalysisValues = showRanges ? Object.values(TypeAnalysis) : [TypeAnalysis.perDay]
  const [typeAnalysis, setTypeAnalysis] = useState(initialTypeAnalysis)
  const [closedPeriod, setClosedPeriod] = useState(initialClosedPeriod)
  const selectedRangeText = useSelectedRangeText(closedPeriod, date)
  const maxSelectableYear = ranges.reduce(
    (maxYear: number | undefined, { range, year }) => (range === closedPeriod && (!maxYear || maxYear < year) ? year : maxYear),
    undefined,
  )
  const years = ranges
    .filter(({ range, year }) => range === Ranges.YEAR && (!maxSelectableYear || year <= maxSelectableYear))
    .map(({ year }) => `${year}`)
  const maxYear = ranges.length ? `${Math.max(...years.map(year => parseInt(year)))}` : null
  const [selectedYear, setSelectedYear] = useState(maxYear || moment(date).format('YYYY'))
  const handleClosedDateChange = (_event: SyntheticEvent, newValue: Range) => {
    const newDate = moment(newValue.start)

    setDate(newDate)
    setSelectedYear(newDate.format('YYYY'))
  }
  const components = { OpenPickerIcon: CalendarIcon }
  const noOptionsText = translate('search.empty')
  const handleTypeAnalysisChange = (_event: MouseEvent<HTMLElement>, value: TypeAnalysis) => {
    if (value) {
      setDate(moment(dateCurrent))
      setTypeAnalysis(value)
      setClosedPeriod(initialClosedPeriod)
    }
  }
  const closedPeriodDate =
    ranges.find(
      ({ range, start, year }) =>
        `${year}` === selectedYear && range === closedPeriod && moment(start).valueOf() === moment(date).valueOf(),
    ) || ranges.find(({ range, year }) => `${year}` === selectedYear && range === closedPeriod)
  const validateDate = () => {
    const parsedDate = date?.format(momentValidDateFormat)?.toString()

    return moment(parsedDate).isValid() && moment(parsedDate).isSameOrBefore(moment(maxDate))
  }
  const isValidDate = validateDate()
  const perEndDateRange = ranges.find(
    ({ range, start, end }) => range === closedPeriod && moment(start).isSameOrBefore(date) && moment(end).isSameOrAfter(date),
  )
  const isDisabled = ((selectedRangeText === '-' || !perEndDateRange) && typeAnalysis === TypeAnalysis.perEndDate) || !isValidDate
  const showClosedPeriods = typeAnalysis === TypeAnalysis.perClosedPeriods && showRanges
  const onClose = useCallback(() => {
    toggleModal()
    setDate(moment(dateCurrent))
    setTypeAnalysis(initialTypeAnalysis)
    setClosedPeriod(initialClosedPeriod)
  }, [dateCurrent, initialClosedPeriod, initialTypeAnalysis, toggleModal])
  const requestRange = () => {
    switch (typeAnalysis) {
      case TypeAnalysis.perClosedPeriods:
        return closedPeriodDate

      case TypeAnalysis.perEndDate:
        return perEndDateRange

      case TypeAnalysis.perDay:
      default:
        return {}
    }
  }
  const onApply = () => {
    if (isValidDate) {
      dispatch(
        selectDateRange({
          range: requestRange(),
          closedPeriod,
          dateCurrent: moment(date).format(momentValidDateFormat),
          typeAnalysis,
        }),
      )
    }
  }
  function CalendarIcon() {
    const classes = useStyles({ isValidDate })

    return <div className={classNames('fm fm-calendar', classes.calendarIcon)} />
  }
  const handleRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClosedPeriod((event.target as HTMLInputElement).value as ClosedPeriods)
  }
  const handleYearChange = useCallback(
    (_event: any, value: string) => {
      const newDate = ranges.find(({ range, year }) => `${year}` === selectedYear && range === closedPeriod)

      setDate(moment(newDate?.start))
      setSelectedYear(value)
    },
    [closedPeriod, ranges, selectedYear],
  )

  useEffect(() => {
    const year = parseInt(selectedYear, 10)

    if (showClosedPeriods && maxSelectableYear && year !== maxSelectableYear && maxSelectableYear < year) {
      handleYearChange(null, `${maxSelectableYear}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedPeriod])

  return (
    <Dialog fullWidth maxWidth="sm" TransitionComponent={Transition} open={isOpen} onClose={onClose}>
      <DialogTitle>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          {translate('date.datePicker')}
          <CloseButton onClick={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb="0.5rem">{translate('date.typeAnalysis')}</Box>
        <ToggleButtonGroup color="primary" value={typeAnalysis} exclusive onChange={handleTypeAnalysisChange}>
          {typeAnalysisValues.map(value => (
            <ToggleButton key={value} value={value}>
              {translate(`date.${value}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {typeAnalysis === TypeAnalysis.perDay && (
          <>
            <Box mb="0.5rem" mt="1rem">
              {translate('date.pickedDate')}
            </Box>
            <DatePicker
              components={components}
              inputFormat="DD.MM.YYYY (dddd)"
              maxDate={maxDate}
              onChange={newValue => setDate(moment(newValue))}
              renderInput={params => <TextField fullWidth {...params} error={!isValidDate} />}
              value={date}
            />
          </>
        )}

        {showClosedPeriods && (
          <>
            <Box mb="2rem" mt="1rem">
              <RadioGroup value={closedPeriod} onChange={handleRangeChange}>
                {CLOSED_PERIODS.map(value => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio />}
                    label={translate<string>(`date.closedPeriods.range.${value}`)}
                  />
                ))}
              </RadioGroup>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Box mb="0.5rem">{translate('date.year')}</Box>
              </Grid>
              <Grid item xs={8}>
                <Box mb="0.5rem">{translate('date.showResultPer', { period: translate(`date.closedPeriod.${closedPeriod}`) })}</Box>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Autocomplete
                  disableClearable
                  getOptionLabel={option => option}
                  noOptionsText={noOptionsText}
                  onChange={handleYearChange}
                  options={years}
                  renderInput={params => <TextField {...params} />}
                  value={selectedYear}
                />
              </Grid>
              <Grid item xs={8}>
                <Autocomplete
                  disableClearable
                  getOptionLabel={option => option[`name${lang.toUpperCase()}` as LocaleLang]}
                  noOptionsText={noOptionsText}
                  onChange={handleClosedDateChange}
                  options={ranges.filter(({ range, year }) => `${year}` === selectedYear && range === closedPeriod)}
                  renderInput={params => <TextField {...params} />}
                  value={closedPeriodDate}
                />
              </Grid>
            </Grid>
          </>
        )}

        {typeAnalysis === TypeAnalysis.perEndDate && showRanges && (
          <>
            <Box mb="0.5rem" mt="1rem">
              {translate('date.selectedEndDate')}
            </Box>
            <DatePicker
              components={components}
              inputFormat="DD.MM.YYYY (dddd)"
              maxDate={maxDate}
              onChange={newValue => setDate(moment(newValue))}
              renderInput={params => <TextField fullWidth {...params} error={!isValidDate} />}
              value={date}
            />
            <Box mb="0.5rem" mt="2rem">
              {translate('date.perEndDateDescription')}
              <b>{selectedRangeText}</b>
            </Box>
            <RadioGroup value={closedPeriod} onChange={handleRangeChange}>
              {FROM_RANGES.map(value => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio />}
                  label={translate<string>(`date.fromDate.${value}`)}
                />
              ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <CommonButton aria-label={translate('date.cancel')} isUpperCase={false} variant="tertiary" onClick={onClose}>
          {translate('date.cancel')}
        </CommonButton>
        <CommonButton aria-label={translate('date.apply')} disabled={isDisabled} isUpperCase={false} onClick={onApply}>
          {translate('date.apply')}
        </CommonButton>
      </DialogActions>
    </Dialog>
  )
}

export default DatePickerDialog
